import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../../components/MainLayout';
import { getStoredUser } from '../../services/authService';
import {
  fetchAnnoClasses, fetchAnnoStats, fetchAnnoImages, fetchAnnoFolders,
  fetchAnnoImage, saveAnnoImage, exportAnnoDataset, fetchAnnoExportList, deleteAnnoExport, getAiServiceOrigin
} from '../../services/api';
import {
  parseScreeningCsvText, buildScreeningWorkQueue, filterWorkQueueByType, countWorkQueueByTypeFilter,
  isWorkItemRemaining, findNextRemainingWorkItem, collectWorkQueueFolderNameList, selectDrawClassForWorkItem,
  WORK_QUEUE_TYPE_FILTER_OPTION_LIST,
} from './screeningWorkQueue';
import ManagementHeader from '../management/ManagementHeader';
import { useTranslation } from '../../context/LanguageContext';
import '../management/management.css';
import './LabelingPage.css';

/* 비전 결함 라벨링 도구 (YOLO26-seg 학습셋 구축) — 폴리곤(외곽선) 방식
 * - 이미지 위 클릭으로 점 추가 → 첫 점 근처 클릭/Enter/완료 버튼으로 폴리곤 닫기
 * - 좌표는 정규화(0~1) 저장 → 백엔드가 YOLO-seg 라벨로 내보냄. 박스는 폴리곤에서 파생 가능. */

const CLASS_COLORS = {
  FLASH: '#EF4444', WELDLINE: '#F59E0B', SHORT: '#3B82F6', SINK: '#A855F7',
};
const STATUS_LABEL = { pending: '대기', labeled: '완료', reviewed: '검수', skipped: '사용제외' };
// 이미지 분류(전체) 선택지. 결함 유형은 폴리곤마다 따로 기록하므로 이미지 단위로는 4가지로만 나눈다.
const IMAGE_CLASS_OPTION_LIST = [
  { value: 'OK', label: '양품' },
  { value: 'DEFECT', label: '불량' },
  { value: 'UNDECIDABLE', label: '판정불가' },
  { value: 'EXCLUDED', label: '사용제외' },
];
// 분류별 저장 상태. 내보내기는 완료(labeled)·검수(reviewed)만 대상으로 하므로,
// 판정불가는 폴리곤 없는 정상 이미지로 섞이지 않게 대기(pending)로 남기고
// 사용제외는 skipped 상태로 저장한다.
const IMAGE_CLASS_SAVE_STATUS = { OK: 'labeled', DEFECT: 'labeled', UNDECIDABLE: 'pending', EXCLUDED: 'skipped' };

/* DB에 저장된 이미지 분류를 선택지 값으로 바꾼다.
 * 예전에는 결함 유형 코드(FLASH 등)를 이미지 분류로 저장했으므로, 선택지에 없는 값은 불량으로 본다. */
function convertStoredImageClassToOption(storedImageClass) {
  if (!storedImageClass) return '';
  const isKnownOption = IMAGE_CLASS_OPTION_LIST.some(option => option.value === storedImageClass);
  return isKnownOption ? storedImageClass : 'DEFECT';
}

// 선별 작업 목록을 브라우저에 보관하는 키. 새로고침해도 목록이 유지되고, 상태는 서버에서 다시 읽는다.
const SCREENING_WORK_QUEUE_STORAGE_KEY = 'labeling.screeningWorkQueue.v1';
// 폴더별 이미지 상태를 한 번에 읽는 최대 개수(백엔드 limit 최대값)
const IMAGE_STATUS_PAGE_SIZE = 1000;

/* 브라우저에 보관한 선별 작업 목록을 읽는다. 없거나 읽을 수 없으면(사생활 보호 모드 등) 빈 목록. */
function readStoredScreeningWorkQueue() {
  try {
    const storedText = window.localStorage.getItem(SCREENING_WORK_QUEUE_STORAGE_KEY);
    const storedWorkQueue = storedText ? JSON.parse(storedText) : [];
    return Array.isArray(storedWorkQueue) ? storedWorkQueue : [];
  } catch (storageError) {
    return [];
  }
}

/* 선별 작업 목록을 브라우저에 보관한다. 빈 목록이면 지운다. 저장할 수 없어도 화면은 그대로 동작한다. */
function writeStoredScreeningWorkQueue(workQueue) {
  try {
    if (workQueue.length === 0) window.localStorage.removeItem(SCREENING_WORK_QUEUE_STORAGE_KEY);
    else window.localStorage.setItem(SCREENING_WORK_QUEUE_STORAGE_KEY, JSON.stringify(workQueue));
  } catch (storageError) {
    // 보관 실패는 무시한다(다음에 CSV 를 다시 불러오면 됨)
  }
}

/* 선별 유형 코드를 화면 표시 이름으로 바꾼다. */
function getWorkQueueTypeLabel(defectType) {
  return WORK_QUEUE_TYPE_FILTER_OPTION_LIST.find(option => option.value === defectType)?.label || defectType;
}

/* 저장 전에 이미지 분류가 저장 가능한지 확인한다. 문제가 있으면 안내 문구, 없으면 빈 문자열을 돌려준다. */
function findSaveValidationMessage(imageClassToSave, polygonCount) {
  if (!imageClassToSave) return '이미지 분류(양품/불량/판정불가/사용제외)를 먼저 선택하세요';
  if (imageClassToSave === 'DEFECT' && polygonCount === 0) {
    return '불량은 결함을 1개 이상 그려야 저장할 수 있습니다 (그리지 않으면 정상 이미지로 내보내짐)';
  }
  return '';
}

function LabelingPage() {
  const { t } = useTranslation();
  const user = getStoredUser();
  const labeler = user?.username || user?.userId || user?.name || 'unknown';

  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [qFilter, setQFilter] = useState('');       // 서버로 보내는 검색어(확정된 값)
  const [qDraft, setQDraft] = useState('');          // 입력창 임시값
  // 폴더 선택 드롭다운 표시용 목록(폴더명 + 폴더별 이미지 개수)
  const [folderOptionList, setFolderOptionList] = useState([]);
  // 사용자가 드롭다운에서 고른 폴더명. 빈 문자열이면 "아직 폴더를 선택하지 않은 상태"를 의미하며,
  // 이 경우 이미지 목록 전체(약 5천여 장)를 불러오지 않는다.
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 100;
  const [current, setCurrent] = useState(null);
  const [polygons, setPolygons] = useState([]);       // [{cls, points:[[x,y],...]}]
  const [draft, setDraft] = useState([]);             // 그리는 중 점들
  const [imageClass, setImageClass] = useState('');
  const [activeClass, setActiveClass] = useState('FLASH');
  const [selected, setSelected] = useState(-1);
  const [cursor, setCursor] = useState(null);         // 미리보기 선용 현재 커서 위치
  const [imgDim, setImgDim] = useState({ w: 100, h: 75 }); // 이미지 실제 비율(왜곡 방지)
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 }); // 확대/이동
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // 내보내기 실행 시 선택하는 라벨 포맷. 'segmentation'은 YOLOv8/YOLO11/YOLO26용 폴리곤,
  // 'detection'은 RT-DETR처럼 폴리곤을 지원하지 않는 모델을 위한 바운딩박스 포맷이다.
  const [selectedExportFormat, setSelectedExportFormat] = useState('segmentation');
  // 내보낼 폴더. 빈 문자열이면 전체 폴더(원본을 모두 복사하므로 수 GB 가 될 수 있음)
  const [selectedExportFolderName, setSelectedExportFolderName] = useState('');
  // 지난 내보내기 결과 목록(시각, 포맷, 개수, 저장 경로)과 그 목록 패널의 노출 여부.
  const [pastExportRunList, setPastExportRunList] = useState([]);
  const [isPastExportListVisible, setIsPastExportListVisible] = useState(false);
  // 내보내기 요청이 진행 중인지 여부. 진행 중에는 버튼을 비활성화해서, 응답이 오기 전에
  // 사용자가 다시 눌러 거의 동시에 두 번 요청하는(레이스 컨디션으로 중복 방지가 뚫리는)
  // 상황을 막는다.
  const [isExportInProgress, setIsExportInProgress] = useState(false);
  // 내보내기 취소(삭제) 요청이 진행 중인 내보내기 이름. 진행 중에는 취소 버튼을 막아 두 번 요청하지 않게 한다.
  const [deletingExportRunName, setDeletingExportRunName] = useState('');
  // 1차 선별 결과 CSV 로 만든 폴리곤 작업 목록(불량·애매 이미지)과, 목록 보기 여부·유형 필터·남은 것만 보기
  const [screeningWorkQueue, setScreeningWorkQueue] = useState(readStoredScreeningWorkQueue);
  const [isWorkQueueViewActive, setIsWorkQueueViewActive] = useState(() => readStoredScreeningWorkQueue().length > 0);
  const [workQueueTypeFilter, setWorkQueueTypeFilter] = useState('ALL');
  const [isOnlyRemainingWorkShown, setIsOnlyRemainingWorkShown] = useState(false);
  // 작업 목록 이미지의 서버 상태 {이미지 id: {status, image_class, box_count}}
  const [imageStatusInfoById, setImageStatusInfoById] = useState({});

  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const pan = useRef(null);
  const history = useRef([]);   // 실행취소용 스냅샷 스택
  // 저장 요청이 진행 중인지. s 키를 빠르게 두 번 누르면 화면이 다시 그려지기 전에 저장이 두 번
  // 나가 폴리곤이 두 벌 저장될 수 있어, 상태값이 아닌 ref 로 즉시 막는다.
  const isSaveInProgressRef = useRef(false);
  // 현재 이미지에 저장하지 않은 변경(폴리곤·분류)이 있는지. 다른 이미지로 옮길 때 확인에 쓴다.
  const hasUnsavedChangesRef = useRef(false);
  // 가장 마지막 이미지 선택 요청 번호. 빠르게 여러 장을 누르면 늦게 온 이전 응답이 화면을 덮지 않게 한다.
  const latestImageSelectRequestNumberRef = useRef(0);

  /* 변경 직전 상태를 히스토리에 저장 (Ctrl+Z 로 복원) — 모든 편집 직전에 불리므로 "저장 안 한 변경 있음"도 표시 */
  const pushHist = () => {
    hasUnsavedChangesRef.current = true;
    history.current.push({
      polygons: polygons.map(p => ({ cls: p.cls, points: p.points.map(pt => [...pt]) })),
      draft: draft.map(pt => [...pt]),
    });
    if (history.current.length > 200) history.current.shift();
  };
  const undo = () => {
    const h = history.current.pop();
    if (!h) return;
    setPolygons(h.polygons);
    setDraft(h.draft);
    setSelected(-1);
    syncImageClassWithPolygonCount(h.polygons.length);
  };

  /* 폴리곤 수가 바뀐 뒤 이미지 분류를 맞춘다.
   * 폴리곤이 1개라도 있으면 불량으로 자동 선택하고, 모두 지워졌는데 분류가 불량이면
   * 다시 고르도록 선택 전(-)으로 되돌린다. */
  const syncImageClassWithPolygonCount = (polygonCount) => {
    if (polygonCount > 0) { setImageClass('DEFECT'); return; }
    setImageClass(previousImageClass => (previousImageClass === 'DEFECT' ? '' : previousImageClass));
  };

  /* 이미지를 캔버스에 맞춰 중앙 배치(scale 1 기준 fit) */
  const fitView = useCallback(() => {
    const c = canvasRef.current, im = imgRef.current;
    if (!c || !im || !im.naturalWidth) return;
    const cw = c.clientWidth, ch = c.clientHeight;
    const s = Math.min(cw / im.naturalWidth, ch / im.naturalHeight) * 0.97;
    setView({ scale: s, x: (cw - im.naturalWidth * s) / 2, y: (ch - im.naturalHeight * s) / 2 });
  }, []);

  const zoomBtn = (factor) => {
    const c = canvasRef.current, im = imgRef.current;
    if (!c || !im) return;
    const cw = c.clientWidth, ch = c.clientHeight;
    setView(v => {
      const ns = Math.min(Math.max(v.scale * factor, 0.1), 20);
      // 캔버스 중앙 기준 확대
      const fx = (cw / 2 - v.x) / (im.naturalWidth * v.scale);
      const fy = (ch / 2 - v.y) / (im.naturalHeight * v.scale);
      return { scale: ns, x: cw / 2 - fx * im.naturalWidth * ns, y: ch / 2 - fy * im.naturalHeight * ns };
    });
  };

  // 휠 확대(커서 위치 기준) — passive 아님으로 등록해 스크롤 방지
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const onWheel = (e) => {
      const im = imgRef.current;
      if (!im || !im.naturalWidth) return;
      e.preventDefault();
      const cR = c.getBoundingClientRect();
      const iR = im.getBoundingClientRect();
      const fx = (e.clientX - iR.left) / iR.width;
      const fy = (e.clientY - iR.top) / iR.height;
      setView(v => {
        const ns = Math.min(Math.max(v.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), 0.1), 20);
        return {
          scale: ns,
          x: (e.clientX - cR.left) - fx * im.naturalWidth * ns,
          y: (e.clientY - cR.top) - fy * im.naturalHeight * ns,
        };
      });
    };
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
  }, [current]);

  // 가운데(휠) 버튼 드래그로 이동 — 시작값을 지역 캡처하고 window 리스너로 처리(영역 밖에서도 안전)
  const onPanDown = (e) => {
    if (e.button !== 1) return;
    e.preventDefault();
    pan.current = true;
    const sx = e.clientX, sy = e.clientY, ox = view.x, oy = view.y;
    const move = (ev) => setView(v => ({ ...v, x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) }));
    const up = () => {
      pan.current = false;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // 폴더 선택 드롭다운에 표시할 폴더 목록(폴더명 + 개수)을 서버에서 가져오는 함수.
  // 이 함수는 "폴더 목록을 가져와 상태에 반영한다"는 역할 하나만 담당한다.
  // 주의: /api/anno/folders는 스스로 재스캔(sync)을 하지 않으므로, 재스캔을 트리거하는
  // fetchAnnoStats() 이후에 호출해야 방금 들어온 새 폴더가 목록에 반영된다.
  // (loadFolderOptionList와 fetchAnnoStats를 서로 독립된 병렬 요청으로 두면, 스캔이 끝나기
  // 전에 폴더 목록을 먼저 읽어와 새 폴더가 누락되는 레이스 컨디션이 생기기 때문에
  // loadList 안에서 fetchAnnoStats 다음 순서로만 호출한다.)
  const loadFolderOptionList = useCallback(async () => {
    const folderListResponse = await fetchAnnoFolders();
    setFolderOptionList(folderListResponse?.folders || []);
  }, []);

  const loadList = useCallback(async () => {
    try {
      const [cls, st] = await Promise.all([fetchAnnoClasses(), fetchAnnoStats()]);
      // 응답이 비어도 화면이 멈추지 않게 배열로 맞춘다 (AI 서비스가 내려가 있으면 null 이 온다)
      const classList = Array.isArray(cls) ? cls : [];
      setClasses(classList);
      setStats(st || null);
      if (classList.length && !classList.find(c => c.code === activeClass)) setActiveClass(classList[0].code);

      // fetchAnnoStats()가 서버에서 폴더를 재스캔한 뒤이므로, 이 시점에 폴더 목록을
      // 가져와야 방금 들어온 새 파일/폴더가 개수에 정확히 반영된다.
      await loadFolderOptionList();

      // 폴더를 아직 선택하지 않았으면 이미지 목록 API를 호출하지 않고 비워둔다.
      // (선택 없이 호출하면 약 5천여 장이 한 번에 잡혀 화면이 무거워지기 때문)
      if (!selectedFolderName) {
        setImages([]);
        setTotal(0);
        return;
      }
      const list = await fetchAnnoImages(statusFilter, pageSize, page * pageSize, qFilter);
      setImages(list?.images || []);
      setTotal(list?.total || 0);
    } catch (e) { setMsg('목록 로딩 실패: ' + e.message); }
  }, [statusFilter, qFilter, page, selectedFolderName, loadFolderOptionList]); // eslint-disable-line

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setPage(0); }, [statusFilter, qFilter]);

  // 사용자가 드롭다운에서 폴더를 선택했을 때의 동작.
  // 선택한 폴더명을 검색창(qDraft/qFilter)에 그대로 채워서, 기존 검색 로직을 그대로 재사용해
  // 그 폴더에 속한 이미지만 보이도록 한다.
  const handleFolderSelectionChange = (changeEvent) => {
    const chosenFolderName = changeEvent.target.value;
    setSelectedFolderName(chosenFolderName);
    // 검색은 web_file 전체에 대한 부분일치라서, 폴더명 뒤에 구분자('/')를 붙여 넣지 않으면
    // 다른 폴더의 파일명 안에 같은 문자열이 우연히 들어있을 때 잘못 섞여 보일 수 있다.
    // 예: 'IV0000' 폴더를 검색해도 'IV00005/...' 파일이 걸리는 것을 막기 위한 경계 처리.
    const searchTextForChosenFolder = chosenFolderName ? `${chosenFolderName}/` : '';
    setQDraft(searchTextForChosenFolder);
    setQFilter(searchTextForChosenFolder);
  };

  // '새로고침' 버튼 클릭 시 폴더 목록과 현재 조건의 이미지 목록을 함께 다시 불러온다.
  // (loadList가 내부적으로 순서를 지켜 폴더 목록까지 갱신하므로 이것 하나만 호출하면 된다.)
  const handleRefreshButtonClick = () => {
    loadList();
    refreshWorkQueueImageStatus(screeningWorkQueue);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const commitSearch = () => setQFilter(qDraft.trim());

  /* 이미지를 열어 폴리곤·분류를 불러온다.
   * 저장하지 않은 변경이 있으면 먼저 확인하고, 늦게 도착한 이전 요청의 응답은 버린다.
   * 선별 작업 목록의 이미지면 선별 때 고른 유형을 그릴 결함 유형으로, 불량이면 분류를 불량으로 미리 선택한다. */
  const selectImage = async (id) => {
    if (hasUnsavedChangesRef.current && current && current.id !== id
      && !window.confirm('저장하지 않은 폴리곤·분류 변경이 있습니다. 저장하지 않고 다른 이미지로 이동할까요?')) {
      return;
    }
    latestImageSelectRequestNumberRef.current += 1;
    const imageSelectRequestNumber = latestImageSelectRequestNumberRef.current;
    setMsg('');
    try {
      const d = await fetchAnnoImage(id);
      if (imageSelectRequestNumber !== latestImageSelectRequestNumberRef.current) return;
      setSelected(-1); setDraft([]); history.current = [];
      hasUnsavedChangesRef.current = false;
      setCurrent(d);
      const loadedPolygonList = (d.polygons || []).map(p => ({ cls: p.cls, points: p.points }));
      setPolygons(loadedPolygonList);
      const workItem = screeningWorkQueue.find(item => item.annoImageId === d.id);
      const storedImageClass = convertStoredImageClassToOption(d.image_class);
      // 폴리곤이 있으면 항상 불량, 분류가 비어 있고 선별에서 불량으로 본 이미지도 불량으로 미리 선택
      const isDefectSelected = loadedPolygonList.length > 0
        || (!storedImageClass && workItem?.screeningResult === 'DEFECT');
      setImageClass(isDefectSelected ? 'DEFECT' : storedImageClass);
      const drawClassForWorkItem = selectDrawClassForWorkItem(workItem, classes.map(c => c.code));
      if (drawClassForWorkItem) setActiveClass(drawClassForWorkItem);
      setImageStatusInfoById(previous => ({
        ...previous, [d.id]: { status: d.status, image_class: d.image_class, box_count: loadedPolygonList.length },
      }));
    } catch (e) {
      if (imageSelectRequestNumber === latestImageSelectRequestNumberRef.current) setMsg('이미지 로딩 실패: ' + e.message);
    }
  };

  /* 작업 목록에 있는 폴더들의 이미지 상태를 서버에서 읽어 작업 목록 이미지의 상태로 반영한다. */
  const loadWorkQueueImageStatus = useCallback(async (workQueue) => {
    const workImageIdSet = new Set(workQueue.map(workItem => workItem.annoImageId));
    const loadedStatusInfoById = {};
    for (const folderName of collectWorkQueueFolderNameList(workQueue)) {
      let offset = 0;
      let totalImageCount = 0;
      do {
        const statusPage = await fetchAnnoImages('all', IMAGE_STATUS_PAGE_SIZE, offset, `${folderName}/`);
        (statusPage.images || []).forEach(image => {
          if (workImageIdSet.has(image.id)) {
            loadedStatusInfoById[image.id] = { status: image.status, image_class: image.image_class, box_count: image.box_count };
          }
        });
        totalImageCount = statusPage.total || 0;
        offset += IMAGE_STATUS_PAGE_SIZE;
      } while (offset < totalImageCount);
    }
    setImageStatusInfoById(loadedStatusInfoById);
  }, []);

  /* 작업 목록 상태 읽기를 실행하고, 실패하면 안내만 한다(목록 자체는 그대로 쓸 수 있음). */
  const refreshWorkQueueImageStatus = useCallback(async (workQueue) => {
    if (workQueue.length === 0) return;
    try {
      await loadWorkQueueImageStatus(workQueue);
    } catch (statusError) {
      setMsg('작업 목록 상태 읽기 실패: ' + statusError.message);
    }
  }, [loadWorkQueueImageStatus]);

  // 화면을 열 때 브라우저에 보관된 작업 목록이 있으면 상태를 서버에서 다시 읽는다(다른 PC에서 한 작업 반영)
  useEffect(() => { refreshWorkQueueImageStatus(screeningWorkQueue); }, []); // eslint-disable-line

  /* "선별 결과 불러오기"로 고른 CSV 파일을 읽어 작업 목록을 만든다. */
  const handleScreeningCsvFileChange = async (changeEvent) => {
    const screeningCsvFile = changeEvent.target.files && changeEvent.target.files[0];
    // 같은 파일을 다시 골라도 다시 읽히도록 선택값을 비운다
    changeEvent.target.value = '';
    if (!screeningCsvFile) return;
    try {
      const workQueue = buildScreeningWorkQueue(parseScreeningCsvText(await screeningCsvFile.text()));
      if (workQueue.length === 0) { setMsg('선별 결과에 불량·애매 이미지가 없습니다'); return; }
      setScreeningWorkQueue(workQueue);
      writeStoredScreeningWorkQueue(workQueue);
      setWorkQueueTypeFilter('ALL');
      setIsWorkQueueViewActive(true);
      setMsg(`선별 작업 목록 ${workQueue.length}장을 불러왔습니다`);
      await refreshWorkQueueImageStatus(workQueue);
    } catch (csvError) {
      setMsg('선별 결과 불러오기 실패: ' + csvError.message);
    }
  };

  /* 작업 목록을 지우고 폴더 목록 보기로 돌아간다. */
  const handleClearWorkQueueClick = () => {
    if (!window.confirm('선별 작업 목록을 지울까요? (라벨링한 내용은 지워지지 않습니다)')) return;
    setScreeningWorkQueue([]);
    writeStoredScreeningWorkQueue([]);
    setImageStatusInfoById({});
    setIsWorkQueueViewActive(false);
  };

  // 유형 필터를 적용한 작업 목록(저장 후 다음 이미지 찾기에 쓰임)과, 남은 것만 보기까지 적용한 화면 표시용 목록
  const typeFilteredWorkQueue = filterWorkQueueByType(screeningWorkQueue, workQueueTypeFilter);
  const displayedWorkQueue = isOnlyRemainingWorkShown
    ? typeFilteredWorkQueue.filter(workItem => isWorkItemRemaining(imageStatusInfoById[workItem.annoImageId]))
    : typeFilteredWorkQueue;
  const workQueueCountByTypeFilter = countWorkQueueByTypeFilter(screeningWorkQueue);
  const remainingWorkCount = screeningWorkQueue.filter(workItem => isWorkItemRemaining(imageStatusInfoById[workItem.annoImageId])).length;
  const currentWorkItem = current ? screeningWorkQueue.find(workItem => workItem.annoImageId === current.id) : null;

  const relPos = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    return {
      x: Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1),
      y: Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1),
    };
  };

  const finishDraft = useCallback(() => {
    setDraft(d => {
      if (d.length >= 3) {
        setPolygons(prev => [...prev, { cls: activeClass, points: d }]);
        // 결함을 1개라도 그리면 이미지 분류는 불량
        setImageClass('DEFECT');
      }
      return [];
    });
  }, [activeClass]);
  const finishUser = () => { if (draft.length >= 3) { pushHist(); finishDraft(); } };

  const onClick = (e) => {
    if (!current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const p = {
      x: Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1),
      y: Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1),
    };
    pushHist();
    // 첫 점 근처(화면상 12px 이내)면 닫기 — 줌 배율과 무관
    if (draft.length >= 3) {
      const d0 = draft[0];
      const dxs = (p.x - d0[0]) * rect.width, dys = (p.y - d0[1]) * rect.height;
      if (Math.hypot(dxs, dys) < 12) { finishDraft(); return; }
    }
    setDraft(prev => [...prev, [p.x, p.y]]);
  };

  const deletePoly = (i) => {
    pushHist();
    setPolygons(prev => prev.filter((_, idx) => idx !== i));
    setSelected(-1);
    syncImageClassWithPolygonCount(polygons.length - 1);
  };
  const changePolyClass = (i, cls) => { pushHist(); setPolygons(prev => prev.map((p, idx) => idx === i ? { ...p, cls } : p)); };

  /* 저장 & 다음: 이미지 분류에 맞는 상태로 저장한다(양품·불량=완료, 판정불가=대기, 사용제외=skipped). */
  const save = async () => {
    if (!current || isSaveInProgressRef.current) return;
    // 그리던 중이면 자동 마감
    let polys = polygons;
    if (draft.length >= 3) { polys = [...polygons, { cls: activeClass, points: draft }]; }
    // 폴리곤이 1개라도 있으면 분류는 항상 불량
    const imageClassToSave = polys.length > 0 ? 'DEFECT' : imageClass;
    const validationMessage = findSaveValidationMessage(imageClassToSave, polys.length);
    if (validationMessage) { setMsg(validationMessage); return; }
    const nextStatus = IMAGE_CLASS_SAVE_STATUS[imageClassToSave];
    isSaveInProgressRef.current = true;
    setSaving(true); setMsg('');
    try {
      const savedImageId = current.id;
      await saveAnnoImage(savedImageId, { image_class: imageClassToSave || null, status: nextStatus, labeler, polygons: polys });
      hasUnsavedChangesRef.current = false;
      const savedStatusInfo = { status: nextStatus, image_class: imageClassToSave || null, box_count: polys.length };
      setImageStatusInfoById(previous => ({ ...previous, [savedImageId]: savedStatusInfo }));
      setMsg('저장됨');
      // 다음 이미지는 저장 전 목록 기준으로 찾는다(loadList 후에도 이 함수 안의 images 는 이전 값이므로)
      const nextImageId = findNextImageIdAfterSave(savedImageId, savedStatusInfo);
      await loadList();
      if (nextImageId) selectImage(nextImageId);
      else { setDraft([]); setMsg('저장됨 — 남은 작업이 없습니다'); }
    } catch (e) { setMsg('저장 실패: ' + e.message); }
    finally { isSaveInProgressRef.current = false; setSaving(false); }
  };

  /* 저장한 뒤 열 다음 이미지 id 를 찾는다. 없으면 null.
   * 작업 목록 보기에서는 유형 필터를 적용한 작업 목록에서 남은 작업을, 폴더 보기에서는 현재 페이지의
   * 다음 대기 이미지를 고른다. 방금 저장한 이미지(판정불가는 대기로 남음)는 다시 고르지 않는다. */
  const findNextImageIdAfterSave = (savedImageId, savedStatusInfo) => {
    if (isWorkQueueViewActive) {
      const statusInfoById = { ...imageStatusInfoById, [savedImageId]: savedStatusInfo };
      const nextWorkItem = findNextRemainingWorkItem(typeFilteredWorkQueue, savedImageId, statusInfoById);
      return nextWorkItem ? nextWorkItem.annoImageId : null;
    }
    const savedImageIndex = images.findIndex(im => im.id === savedImageId);
    const nextImage = images.slice(savedImageIndex + 1).find(im =>
      im.id !== savedImageId && im.status === 'pending' && im.image_class !== 'UNDECIDABLE');
    return nextImage ? nextImage.id : null;
  };

  // 지난 내보내기 결과 목록을 서버에서 가져와 상태에 반영한다. (한 가지 역할: 목록 불러오기)
  const loadPastExportRunList = useCallback(async () => {
    try {
      const exportListResponse = await fetchAnnoExportList();
      setPastExportRunList(exportListResponse.exports || []);
    } catch (exportListError) {
      setMsg('지난 내보내기 목록 로딩 실패: ' + exportListError.message);
    }
  }, []);

  // 사용자가 내보내기 포맷(세그멘테이션/디텍션)을 바꿀 때의 동작. (한 가지 역할: 선택값 반영)
  const handleExportFormatSelectionChange = (changeEvent) => {
    setSelectedExportFormat(changeEvent.target.value);
  };

  // '내보내기' 버튼 클릭 시 현재 선택된 포맷으로 내보내기를 실행하고, 완료 메시지를 보여준 뒤
  // 지난 내보내기 목록도 함께 새로고침한다. (한 가지 역할: 내보내기 실행 흐름 처리)
  const handleExportButtonClick = async () => {
    // 이미 요청이 진행 중이면 다시 실행하지 않는다. 버튼이 비활성화돼 있어 보통 도달하지
    // 않지만, 응답이 오기 전에 사용자가 빠르게 두 번 눌러 거의 동시에 두 요청이 나가면
    // 서버의 중복 방지 검사가 둘 다 "중복 아님"으로 판단해버릴 수 있어(레이스 컨디션)
    // 한 번 더 안전하게 막아둔다.
    if (isExportInProgress) return;
    // 원본 이미지를 복사하므로(장당 약 3.6MB) 범위를 확인받고 시작한다
    const isExportConfirmed = window.confirm(
      `${getExportFolderDisplayName(selectedExportFolderName)} · ${getExportFormatDisplayName(selectedExportFormat)} 로 내보낼까요?\n\n` +
      '완료된 이미지의 원본을 복사하므로 전체 폴더는 수 GB 가 될 수 있습니다.');
    if (!isExportConfirmed) return;
    setIsExportInProgress(true);
    setMsg('내보내는 중...');
    try {
      const exportResult = await exportAnnoDataset(selectedExportFormat, selectedExportFolderName);
      const exportSummaryText =
        `이미지 ${exportResult.exported_image_count}장 / ` +
        `폴리곤 ${exportResult.exported_polygon_count}개 → ${exportResult.export_dir}`;
      // 서버가 마지막 내보내기 이후 라벨링이 바뀌지 않았다고 판단하면(is_duplicate),
      // 새로 만들지 않고 기존 결과를 재사용했다는 것을 알 수 있게 메시지를 다르게 보여준다.
      if (exportResult.is_duplicate) {
        setMsg(`마지막 내보내기 이후 변경된 라벨링이 없어 기존 결과를 그대로 사용합니다: ${exportSummaryText}`);
      } else {
        setMsg(`내보내기 완료: ${exportSummaryText}`);
      }
      await loadPastExportRunList();
    } catch (exportError) {
      setMsg('내보내기 실패: ' + exportError.message);
    } finally {
      setIsExportInProgress(false);
    }
  };

  // 지난 내보내기 목록의 '내보내기 취소' 버튼: 확인 후 그 내보내기 폴더(복사본)를 지우고 목록을 다시 읽는다.
  // (한 가지 역할: 내보내기 취소 흐름 처리) 라벨링 DB 와 원본 이미지는 지워지지 않는다.
  const handleDeleteExportButtonClick = async (exportRunInfo) => {
    if (deletingExportRunName) return;
    const isDeleteConfirmed = window.confirm(
      `이 내보내기를 취소할까요?\n${getExportedAtDisplayText(exportRunInfo.exported_at)} · ` +
      `${getExportFormatDisplayName(exportRunInfo.export_format)} · ${getExportFolderDisplayName(exportRunInfo.folder_name)} · ` +
      `이미지 ${exportRunInfo.exported_image_count}장\n\n` +
      '내보낸 폴더(이미지 복사본·라벨 파일)만 지워지고, 라벨링한 내용(DB)과 원본 이미지는 그대로 남습니다.');
    if (!isDeleteConfirmed) return;
    setDeletingExportRunName(exportRunInfo.export_run_name);
    try {
      await deleteAnnoExport(exportRunInfo.export_run_name);
      setMsg(`내보내기 취소됨: ${exportRunInfo.export_run_name}`);
      await loadPastExportRunList();
    } catch (deleteError) {
      setMsg('내보내기 취소 실패: ' + deleteError.message);
    } finally {
      setDeletingExportRunName('');
    }
  };

  // '지난 내보내기 목록' 버튼 클릭 시 목록 패널을 열고 닫는다. 열릴 때는 최신 목록을 가져온다.
  // (한 가지 역할: 목록 패널 노출 여부 전환)
  const handleTogglePastExportListButtonClick = () => {
    const willBecomeVisible = !isPastExportListVisible;
    setIsPastExportListVisible(willBecomeVisible);
    if (willBecomeVisible) {
      loadPastExportRunList();
    }
  };

  // 내보내기 폴더 이름을 화면 표시 이름으로 바꿔준다. (한 가지 역할: 표시용 이름 변환) 비어 있으면 전체 폴더
  const getExportFolderDisplayName = (exportFolderName) => (exportFolderName ? `${exportFolderName} 폴더` : '전체 폴더');

  // 내보내기 포맷 코드값을 화면에 보여줄 한글 이름으로 바꿔준다. (한 가지 역할: 표시용 이름 변환)
  const getExportFormatDisplayName = (exportFormatCode) => (
    exportFormatCode === 'segmentation' ? '세그멘테이션(폴리곤)' : '디텍션(바운딩박스)'
  );

  // ISO 형식 시각 문자열을 화면에 보여줄 한국어 날짜/시간 문자열로 바꿔준다. (한 가지 역할: 표시용 시각 변환)
  const getExportedAtDisplayText = (isoDateTimeText) => new Date(isoDateTimeText).toLocaleString('ko-KR');

  // 키보드: Ctrl+Z 실행취소, 1~4 클래스, Enter 닫기, Esc 취소, Backspace 점 삭제, Del 폴리곤 삭제, s 저장
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      // '지난 내보내기 목록' 패널이 열려 있는 동안에는 라벨링 단축키(숫자키/Enter/s 등)를
      // 무시한다. 안 그러면 목록을 보다가 배경의 라벨링 화면에 실수로 저장/클래스 변경이
      // 일어날 수 있다.
      if (isPastExportListVisible) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if (e.ctrlKey || e.metaKey) return;
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= classes.length) setActiveClass(classes[n - 1].code);
      else if (e.key === 'Enter') { e.preventDefault(); finishUser(); }
      else if (e.key === 'Escape') { if (draft.length) { pushHist(); setDraft([]); } }
      else if (e.key === 'Backspace') { if (draft.length) { pushHist(); setDraft(d => d.slice(0, -1)); } }
      else if (e.key === 'Delete' && selected >= 0) deletePoly(selected);
      else if (e.key.toLowerCase() === 's') save();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line

  const clsLabel = (code) => classes.find(c => c.code === code)?.label_ko || code;
  const W = imgDim.w, H = imgDim.h;
  const R = Math.min(W, H) * 0.004;   // 점 기준 반지름(이미지 크기 비례)
  const RS = R / view.scale;          // 줌 무관 일정 화면크기 유지용
  const ptsAttr = (pts) => pts.map(p => `${p[0] * W},${p[1] * H}`).join(' ');

  return (
    <MainLayout>
      <div className="mg-page" style={{ overflow: 'hidden' }}>
      <ManagementHeader rootLabel={t('sidebar.system.title')} icon="settings" crumbs={[{ label: t('sidebar.system.labeling') }]} />
      <div className="lb-root">
        <div className="lb-header">
          <h2>비전 결함 라벨링 <span className="lb-sub">YOLO26 세그멘테이션 학습셋</span></h2>
          <div className="lb-stats">
            {stats && (<>
              <span>전체 {stats.total}</span>
              <span className="lb-pill pending">대기 {stats.pending}</span>
              <span className="lb-pill labeled">완료 {stats.labeled}</span>
              {Object.entries(stats.box_by_class || {}).map(([c, n]) =>
                <span key={c} className="lb-pill" style={{ background: (CLASS_COLORS[c] || '#666') + '33', color: CLASS_COLORS[c] }}>{clsLabel(c)} {n}</span>)}
            </>)}
            <button className="lb-btn" onClick={handleRefreshButtonClick}>새로고침</button>
            <select
              className="lb-export-format-select"
              value={selectedExportFormat}
              onChange={handleExportFormatSelectionChange}
            >
              <option value="segmentation">세그멘테이션(폴리곤)</option>
              <option value="detection">디텍션(바운딩박스)</option>
            </select>
            {/* 내보낼 폴더 — 폴더를 고르면 그 폴더의 완료 이미지만 내보낸다 */}
            <select
              className="lb-export-format-select"
              aria-label="내보낼 폴더"
              value={selectedExportFolderName}
              onChange={changeEvent => setSelectedExportFolderName(changeEvent.target.value)}
            >
              <option value="">전체 폴더</option>
              {folderOptionList.map(folderOption => (
                <option key={folderOption.folder_name} value={folderOption.folder_name}>{folderOption.folder_name}</option>
              ))}
            </select>
            <button className="lb-btn lb-export" onClick={handleExportButtonClick} disabled={isExportInProgress}>
              {isExportInProgress ? '내보내는 중...' : '내보내기'}
            </button>
            <button className="lb-btn" onClick={handleTogglePastExportListButtonClick}>지난 내보내기 목록</button>
          </div>
        </div>

        {/* 지난 내보내기 목록 패널 — 배경을 클릭하면 닫히고, 패널 안 클릭은 전파되지 않는다 */}
        {isPastExportListVisible && (
          <div className="lb-export-list-backdrop" onClick={handleTogglePastExportListButtonClick}>
            <div className="lb-export-list-panel" onClick={(clickEvent) => clickEvent.stopPropagation()}>
              <div className="lb-export-list-header">
                <span>지난 내보내기 목록</span>
                <button className="lb-mini" onClick={handleTogglePastExportListButtonClick}>닫기</button>
              </div>
              <div className="lb-export-list-body">
                {pastExportRunList.length === 0 && (
                  <div className="lb-empty">아직 내보낸 결과가 없습니다.</div>
                )}
                {pastExportRunList.map(exportRunInfo => (
                  <div key={exportRunInfo.export_run_name} className="lb-export-list-row">
                    <span className="lb-export-list-date">{getExportedAtDisplayText(exportRunInfo.exported_at)}</span>
                    <span className="lb-export-list-format">
                      {getExportFormatDisplayName(exportRunInfo.export_format)} · {getExportFolderDisplayName(exportRunInfo.folder_name)}
                    </span>
                    <span className="lb-export-list-count">
                      이미지 {exportRunInfo.exported_image_count}장 / 폴리곤 {exportRunInfo.exported_polygon_count}개
                    </span>
                    <span className="lb-export-list-path">{exportRunInfo.export_dir}</span>
                    <button className="lb-mini lb-export-delete"
                      disabled={Boolean(deletingExportRunName)}
                      onClick={() => handleDeleteExportButtonClick(exportRunInfo)}>
                      {deletingExportRunName === exportRunInfo.export_run_name ? '취소하는 중...' : '내보내기 취소'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="lb-body">
          {/* 좌: 목록(텍스트 전용 — 썸네일 없음, 클릭 시에만 우측에 원본 로딩) */}
          <div className="lb-list">
            {/* 1차 선별 결과(CSV)로 만든 폴리곤 작업 목록 — 불러오면 폴더 목록 대신 불량·애매 이미지만 보인다 */}
            <div className="lb-queue-bar">
              <label className="lb-mini lb-queue-load">
                선별 결과 불러오기
                <input type="file" accept=".csv,text/csv" data-testid="screening-csv-input"
                  onChange={handleScreeningCsvFileChange} hidden />
              </label>
              {screeningWorkQueue.length > 0 && (<>
                <button className={`lb-mini lb-queue-toggle ${isWorkQueueViewActive ? 'active' : ''}`}
                  onClick={() => setIsWorkQueueViewActive(isActive => !isActive)}>
                  {isWorkQueueViewActive ? '폴더 보기' : `작업 목록 (남은 ${remainingWorkCount})`}
                </button>
                <button className="lb-mini lb-queue-clear" onClick={handleClearWorkQueueClick} title="작업 목록 지우기">✕</button>
              </>)}
            </div>
            {isWorkQueueViewActive && screeningWorkQueue.length > 0 ? (<>
              <div className="lb-folder-select-area">
                <label htmlFor="labeling-work-queue-type-select" className="lb-folder-select-label">
                  선별 작업 목록 — 전체 {screeningWorkQueue.length}장 / 남은 {remainingWorkCount}장
                </label>
                <select id="labeling-work-queue-type-select" className="lb-folder-select"
                  value={workQueueTypeFilter} onChange={changeEvent => setWorkQueueTypeFilter(changeEvent.target.value)}>
                  {WORK_QUEUE_TYPE_FILTER_OPTION_LIST.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({workQueueCountByTypeFilter[option.value]})
                    </option>
                  ))}
                </select>
                <label className="lb-queue-check">
                  <input type="checkbox" checked={isOnlyRemainingWorkShown}
                    onChange={changeEvent => setIsOnlyRemainingWorkShown(changeEvent.target.checked)} />
                  남은 것만 보기
                </label>
              </div>
              <div className="lb-rows">
                {displayedWorkQueue.length === 0 && <div className="lb-empty">조건에 맞는 작업이 없습니다.</div>}
                {displayedWorkQueue.map(workItem => {
                  const pathPartList = workItem.relativePath.split('/');
                  const fileName = pathPartList[pathPartList.length - 1];
                  const statusInfo = imageStatusInfoById[workItem.annoImageId];
                  const statusText = !statusInfo ? '-'
                    : statusInfo.image_class === 'UNDECIDABLE' ? '판정불가' : STATUS_LABEL[statusInfo.status];
                  const typeText = workItem.screeningResult === 'UNSURE'
                    ? '애매' : workItem.defectTypeList.map(getWorkQueueTypeLabel).join(', ');
                  return (
                    <button key={workItem.annoImageId}
                      className={`lb-row ${current?.id === workItem.annoImageId ? 'active' : ''} st-${statusInfo?.status || 'pending'}`}
                      onClick={() => selectImage(workItem.annoImageId)}>
                      <span className={`lb-ngdot ${isWorkItemRemaining(statusInfo) ? 'ng' : 'ok'}`} />
                      <span className="lb-row-text">
                        <span className="lb-row-dir">{typeText}</span>
                        <span className="lb-row-file">{fileName}</span>
                      </span>
                      <span className={`lb-row-status st-${statusInfo?.status || 'pending'}`}>
                        {statusText}{statusInfo?.box_count ? ` · ${statusInfo.box_count}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>) : (<>
            <div className="lb-filter">
              {['all', 'pending', 'labeled', 'reviewed'].map(s =>
                <button key={s} className={`lb-fbtn ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? '전체' : STATUS_LABEL[s]}
                </button>)}
            </div>
            {/* 폴더 선택(필수) — 고르기 전에는 이미지 목록이 나타나지 않는다 */}
            <div className="lb-folder-select-area">
              <label htmlFor="labeling-folder-select" className="lb-folder-select-label">
                폴더 선택 (필수)
              </label>
              <select
                id="labeling-folder-select"
                className="lb-folder-select"
                value={selectedFolderName}
                onChange={handleFolderSelectionChange}
              >
                <option value="">폴더를 선택하세요</option>
                {folderOptionList.map(folderOption => (
                  <option key={folderOption.folder_name} value={folderOption.folder_name}>
                    {folderOption.folder_name} ({folderOption.image_count.toLocaleString()})
                  </option>
                ))}
              </select>
            </div>
            <div className="lb-qbar">
              <input className="lb-qinput" placeholder="기기ID/파일명 검색 (예: IV00005, _NG_)"
                value={qDraft} onChange={e => setQDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') commitSearch(); }} />
              <button className="lb-mini" onClick={commitSearch}>검색</button>
            </div>
            <div className="lb-rows">
              {!selectedFolderName && (
                <div className="lb-empty">먼저 위에서 폴더를 선택하세요.</div>
              )}
              {selectedFolderName && images.length === 0 && (
                <div className="lb-empty">조건에 맞는 이미지가 없습니다.</div>
              )}
              {selectedFolderName && images.map(im => {
                const parts = im.web_file.split('/');
                const filename = parts[parts.length - 1];
                const dir = parts.slice(0, -1).join(' / ');
                const isNg = filename.includes('_NG_');
                return (
                  <button key={im.id} className={`lb-row ${current?.id === im.id ? 'active' : ''} st-${im.status}`} onClick={() => selectImage(im.id)}>
                    <span className={`lb-ngdot ${isNg ? 'ng' : 'ok'}`} title={isNg ? 'NG(불량)' : 'OK(양품)'} />
                    <span className="lb-row-text">
                      {dir && <span className="lb-row-dir">{dir}</span>}
                      <span className="lb-row-file">{filename}</span>
                    </span>
                    <span className={`lb-row-status st-${im.status}`}>{im.image_class === 'UNDECIDABLE' ? '판정불가' : STATUS_LABEL[im.status]}{im.box_count ? ` · ${im.box_count}` : ''}</span>
                  </button>
                );
              })}
            </div>
            {selectedFolderName && (
              <div className="lb-pager">
                <button className="lb-mini" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ 이전</button>
                <span className="lb-pager-info">{page + 1} / {totalPages} 페이지 (총 {total.toLocaleString()}장)</span>
                <button className="lb-mini" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>다음 ›</button>
              </div>
            )}
            </>)}
          </div>

          {/* 중앙: 캔버스 */}
          <div className="lb-canvas" ref={canvasRef}>
            {current ? (<>
              <div className="lb-imgwrap"
                style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: '0 0' }}
                onClick={onClick} onMouseDown={onPanDown}
                onMouseMove={(e) => { if (!pan.current) setCursor(relPos(e)); }}
                onMouseLeave={() => setCursor(null)}>
                <img ref={imgRef} src={`${getAiServiceOrigin()}${current.url}`} alt={current.web_file} draggable={false}
                  onLoad={(e) => { setImgDim({ w: e.target.naturalWidth || 100, h: e.target.naturalHeight || 75 }); fitView(); }} />
                <svg className="lb-overlay" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                  <defs>
                    <filter id="lb-glow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="0" stdDeviation={RS * 0.6} floodColor="#000" floodOpacity="0.55" />
                    </filter>
                  </defs>
                  {/* 완료된 폴리곤 */}
                  {polygons.map((pg, i) => {
                    const col = CLASS_COLORS[pg.cls] || '#22c55e';
                    const on = selected === i;
                    return (
                      <g key={i} onClick={(e) => { e.stopPropagation(); setSelected(i); }}
                        style={{ cursor: 'pointer', pointerEvents: draft.length > 0 ? 'none' : 'auto' }} filter="url(#lb-glow)">
                        <polygon points={ptsAttr(pg.points)} fill={col + (on ? '38' : '24')}
                          stroke={col} strokeWidth={on ? 2.5 : 1.75} strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                          style={{ pointerEvents: draft.length > 0 ? 'none' : 'auto' }} />
                        {on && pg.points.map((p, j) => (
                          <circle key={j} cx={p[0] * W} cy={p[1] * H} r={RS * 0.9}
                            fill="#fff" stroke={col} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                        ))}
                      </g>
                    );
                  })}
                  {/* 그리는 중 */}
                  {draft.length > 0 && (
                    <g filter="url(#lb-glow)">
                      {draft.length >= 2 && (
                        <polygon points={ptsAttr(cursor ? [...draft, [cursor.x, cursor.y]] : draft)}
                          fill={(CLASS_COLORS[activeClass]) + '1f'} stroke="none" />
                      )}
                      <polyline points={ptsAttr(cursor ? [...draft, [cursor.x, cursor.y]] : draft)}
                        fill="none" stroke={CLASS_COLORS[activeClass]} strokeWidth={2}
                        strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 4"
                        vectorEffect="non-scaling-stroke" />
                      {draft.map((p, i) => (
                        <g key={i}>
                          <circle cx={p[0] * W} cy={p[1] * H} r={i === 0 ? RS * 1.4 : RS}
                            fill="#fff" stroke={CLASS_COLORS[activeClass]} strokeWidth={1.5}
                            vectorEffect="non-scaling-stroke" />
                          {i === 0 && (
                            <circle cx={p[0] * W} cy={p[1] * H} r={RS * 0.55}
                              fill={CLASS_COLORS[activeClass]} />
                          )}
                        </g>
                      ))}
                    </g>
                  )}
                </svg>
              </div>
              <div className="lb-zoom">
                <button onClick={() => zoomBtn(1.25)} title="확대">+</button>
                <button onClick={() => zoomBtn(0.8)} title="축소">−</button>
                <button onClick={fitView} title="화면 맞춤">맞춤</button>
                <span className="lb-zoom-pct">{Math.round(view.scale * 100)}%</span>
              </div>
            </>) : <div className="lb-placeholder">왼쪽에서 이미지를 선택하세요</div>}
          </div>

          {/* 우: 클래스/폴리곤/저장 */}
          <div className="lb-panel">
            <div className="lb-section">
              <div className="lb-label">그릴 결함 유형 <small>(1~{classes.length})</small></div>
              <div className="lb-classes">
                {classes.map((c, i) => (
                  <button key={c.code} className={`lb-cls ${activeClass === c.code ? 'active' : ''}`}
                    style={{ borderColor: CLASS_COLORS[c.code], color: activeClass === c.code ? '#fff' : CLASS_COLORS[c.code], background: activeClass === c.code ? CLASS_COLORS[c.code] : 'transparent' }}
                    onClick={() => setActiveClass(c.code)}>{i + 1}. {c.label_ko}</button>
                ))}
              </div>
              <div className="lb-drawhint">
                이미지를 클릭해 점을 찍고, <b>첫 점 근처 클릭</b> 또는 <b>Enter</b>로 닫기.<br />
                Esc 취소 · Backspace 점 취소 · Ctrl+Z 실행취소
                {draft.length > 0 && <> · <span style={{ color: CLASS_COLORS[activeClass] }}>그리는 중 {draft.length}점</span>
                  <button className="lb-mini" onClick={finishUser}>완료</button>
                  <button className="lb-mini" onClick={() => setDraft([])}>취소</button></>}
              </div>
            </div>

            <div className="lb-section">
              {currentWorkItem && (
                <div className="lb-screening-hint">
                  선별 판정: {currentWorkItem.screeningResult === 'UNSURE'
                    ? '애매'
                    : `불량 — ${currentWorkItem.defectTypeList.map(getWorkQueueTypeLabel).join(', ') || '유형 없음'}`}
                </div>
              )}
              <div className="lb-label">이미지 분류(전체)</div>
              {/* 폴리곤이 있으면 불량으로 고정(다른 분류는 선택 불가) */}
              <select className="lb-select" value={imageClass} onChange={e => { hasUnsavedChangesRef.current = true; setImageClass(e.target.value); }}>
                <option value="" disabled={polygons.length > 0}>-</option>
                {IMAGE_CLASS_OPTION_LIST.map(option => (
                  <option key={option.value} value={option.value}
                    disabled={polygons.length > 0 && option.value !== 'DEFECT'}>{option.label}</option>
                ))}
              </select>
              <div className="lb-hint">
                {polygons.length > 0
                  ? '결함이 그려져 있어 불량으로 자동 선택됨'
                  : '판정불가는 대기로, 사용제외는 학습 제외로 저장'}
              </div>
            </div>

            <div className="lb-section lb-boxlist">
              <div className="lb-label">폴리곤 {polygons.length}개</div>
              {polygons.map((pg, i) => (
                <div key={i} className={`lb-boxrow ${selected === i ? 'active' : ''}`} onClick={() => setSelected(i)}>
                  <span className="lb-dot" style={{ background: CLASS_COLORS[pg.cls] }} />
                  <select value={pg.cls} onChange={e => changePolyClass(i, e.target.value)}>
                    {classes.map(c => <option key={c.code} value={c.code}>{c.label_ko}</option>)}
                  </select>
                  <span className="lb-ptn">{pg.points.length}점</span>
                  <button className="lb-del" onClick={(e) => { e.stopPropagation(); deletePoly(i); }}>✕</button>
                </div>
              ))}
              {polygons.length === 0 && <div className="lb-hint">이미지 위에서 결함 외곽을 점으로 찍으세요</div>}
            </div>

            <div className="lb-actions">
              <button className="lb-btn primary" disabled={!current || saving} onClick={() => save()}>저장 & 다음 (s)</button>
            </div>
            {msg && <div className="lb-msg">{msg}</div>}
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}

export default LabelingPage;
